import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import inventoryApi from '../../../services/inventoryApi';
import SearchableInventorySupplierDropdown from '../../Common/SearchableInventorySupplierDropdown';
import LoadingSpinner from '../../Common/LoadingSpinner';

/**
 * Raise debit notes from approved supplier returns — one note per selected return.
 * The return already took the goods out of stock and posted the GL; its debit note carries the
 * open amount so it can be set off in supplier payments.
 */
const DebitNoteFromReturns = ({ onClose, onSuccess }) => {
  const [supplier, setSupplier] = useState('');
  const [supplierLabel, setSupplierLabel] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => timer.current && clearTimeout(timer.current);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await inventoryApi.getSupplierReturnsForDebitNote({ supplierId: supplier || undefined, search: search || undefined });
        if (!cancelled) {
          setReturns(res.data || []);
          setSelected(prev => new Set([...prev].filter(id => (res.data || []).some(r => r._id === id))));
        }
      } catch (err) {
        if (!cancelled) { toast.error(err.response?.data?.message || 'Failed to load supplier returns'); setReturns([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [supplier, search]);

  const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '-');

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allSelected = returns.length > 0 && returns.every(r => selected.has(r._id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(returns.map(r => r._id)));
  const selectedTotal = returns.filter(r => selected.has(r._id)).reduce((s, r) => s + (r.openAmount || 0), 0);

  const handleCreate = async () => {
    if (selected.size === 0) { toast.error('Select at least one supplier return'); return; }
    setSaving(true);
    try {
      const res = await inventoryApi.createDebitNotesFromReturns([...selected]);
      setResult(res.data);
      toast.success(res.message || 'Debit notes created');
      if (!res.data?.failed?.length) onSuccess?.();
    } catch (err) {
      const data = err.response?.data;
      if (data?.data) setResult(data.data);
      toast.error(data?.message || 'Failed to create debit notes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/40 overflow-y-auto z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-5xl max-h-[92vh] bg-white shadow-2xl rounded-2xl flex flex-col overflow-hidden">
        <div className="flex-shrink-0 bg-gradient-to-r from-amber-600 to-orange-600 px-6 py-3 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-white">Debit Notes from Supplier Returns</h3>
            <p className="text-xs text-amber-100">Approved returns without a debit note — one debit note per return, set off in supplier payments</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {result ? (
            <div className="space-y-3">
              {(result.created || []).length > 0 && (
                <div className="border border-green-200 bg-green-50 rounded-lg p-3">
                  <p className="text-sm font-semibold text-green-800 mb-1">Created</p>
                  <ul className="text-sm text-green-900 space-y-0.5">
                    {result.created.map(c => <li key={c.id}>{c.returnNoteNumber} → <span className="font-medium">{c.debitNoteNumber}</span> ({fmt(c.amount)})</li>)}
                  </ul>
                </div>
              )}
              {(result.failed || []).length > 0 && (
                <div className="border border-red-200 bg-red-50 rounded-lg p-3">
                  <p className="text-sm font-semibold text-red-800 mb-1">Not created</p>
                  <ul className="text-sm text-red-900 space-y-0.5">
                    {result.failed.map(f => <li key={f.id}>{f.returnNoteNumber || f.id}: {f.message}</li>)}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Supplier</label>
                  <SearchableInventorySupplierDropdown usableFor="goods"
                    value={supplier}
                    displayLabel={supplierLabel}
                    onChange={(val, label) => { setSupplier(val || ''); setSupplierLabel(label || ''); }}
                    placeholder="All suppliers"
                    menuPlacement="down"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Search</label>
                  <div className="relative">
                    <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Return no, GRN no or supplier..."
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg overflow-x-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-8"><LoadingSpinner /><span className="ml-2 text-gray-600">Loading returns...</span></div>
                ) : returns.length === 0 ? (
                  <p className="text-sm text-gray-500 py-8 text-center">No approved supplier returns waiting for a debit note</p>
                ) : (
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
                      <tr>
                        <th className="px-3 py-2 w-8"><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
                        <th className="px-3 py-2 text-left">Return #</th>
                        <th className="px-3 py-2 text-left">Supplier</th>
                        <th className="px-3 py-2 text-left">GRN</th>
                        <th className="px-3 py-2 text-left">Approved</th>
                        <th className="px-3 py-2 text-right">Items</th>
                        <th className="px-3 py-2 text-right">Value</th>
                        <th className="px-3 py-2 text-right">Already Set Off</th>
                        <th className="px-3 py-2 text-right">Debit Note Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {returns.map(r => (
                        <tr key={r._id} className={`cursor-pointer hover:bg-amber-50 ${selected.has(r._id) ? 'bg-amber-50' : ''}`} onClick={() => toggle(r._id)}>
                          <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                            <input type="checkbox" checked={selected.has(r._id)} onChange={() => toggle(r._id)} />
                          </td>
                          <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{r.returnNoteNumber}</td>
                          <td className="px-3 py-2 text-gray-700">{r.supplierName}</td>
                          <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{r.grnNumbers || '-'}</td>
                          <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{fmtDate(r.approvedAt || r.returnDate)}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{r.itemCount} ({r.totalQuantity})</td>
                          <td className="px-3 py-2 text-right">{fmt(r.value)}</td>
                          <td className="px-3 py-2 text-right text-gray-500">{r.setoffAmount ? fmt(r.setoffAmount) : '-'}</td>
                          <td className="px-3 py-2 text-right font-semibold text-amber-700">{fmt(r.openAmount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex-shrink-0 bg-gray-50 px-5 py-3 border-t border-gray-200 flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm text-gray-600">
            {result ? '' : `${selected.size} selected · ${fmt(selectedTotal)}`}
          </span>
          <div className="flex gap-3">
            {result ? (
              <button onClick={() => { onSuccess?.(); }} className="px-4 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg">Done</button>
            ) : (
              <>
                <button onClick={onClose} className="px-4 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">Cancel</button>
                <button onClick={handleCreate} disabled={saving || selected.size === 0}
                  className="px-4 py-1.5 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white text-sm font-medium rounded-lg disabled:opacity-50">
                  {saving ? 'Creating...' : `Create Debit Note${selected.size > 1 ? 's' : ''}`}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DebitNoteFromReturns;
